import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../provider/AuthProvider.tsx";
import SectionHeader from "../../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../../components/Breadcrumbs/Breadcrumb.tsx";
import DatePicker from "../../../components/Forms/DatePicker/DatePicker.tsx";
import FadeAlert, { FadeAlertType } from "../../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../../components/Forms/SelectGroup/SelectGroup.tsx";
import { labelClass, inputClass } from "../../../constants/formStyles.ts";
import type { CalcNavigationState } from "../../../types/calculatorTypes.ts";
import {
  LevInsIntegrationService,
  type LevInsPolicyData,
  type LevInsPremiumResponse,
} from "../../../services/Integrations/LevInsIntegration.ts";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-body dark:text-bodydark text-[11px]">{label}</span>
    <span className="text-xs font-semibold text-black dark:text-white">
      {value ?? "—"}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const LevIns: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();
  const calcState = location.state as CalcNavigationState | undefined;

  const tomorrow = moment().add(1, "day");
  const nextYear = moment().add(1, "year");

  const [form, setForm] = useState(() => {
    // Name parts and sex only make sense for physical persons
    const isPhysical = calcState?.owner?.personType !== "2";
    const nameParts = isPhysical
      ? (calcState?.owner?.name ?? "").split(" ").filter(Boolean)
      : [];
    // EGN digit 9: even = male, odd = female; Lev Ins codes: "1" = female, "2" = male
    const egnSexDigit = isPhysical
      ? Number((calcState?.owner?.pid ?? "")[8])
      : NaN;
    const sexFromEgn = Number.isNaN(egnSexDigit)
      ? "2"
      : egnSexDigit % 2 === 0
        ? "2"
        : "1";
    return {
      // Dates
      fromDate: tomorrow.format("DD-MM-YYYY"),
      toDate: nextYear.format("DD-MM-YYYY"),
      installments: 1,
      // Sticker (required for issue)
      stickerNo: "",
      // Owner extras not in calcState
      gname: nameParts[0] ?? "",
      sname: nameParts.length > 2 ? nameParts[1] : "",
      fname:
        nameParts.length > 2
          ? nameParts.slice(2).join(" ")
          : (nameParts[1] ?? ""),
      sex: sexFromEgn as "1" | "2",
      // Vehicle extras
      usageId: "1",
      fuelTypeId: "B",
      driveRegionId: "1",
      rightHandDrive: false,
    };
  });

  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumResult, setPremiumResult] = useState<LevInsPremiumResponse | null>(null);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issuedData, setIssuedData] = useState<any | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>(FadeAlertType.info);

  const autoCalcRef = useRef(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // ── Build policy data ──────────────────────────────────────────────────────

  const buildPolicyData = (withSticker: boolean): LevInsPolicyData => {
    const owner = calcState?.owner;
    const vehicle = calcState?.vehicle;

    return {
      insr_type: "1000",
      from_date: `${moment(form.fromDate, "DD-MM-YYYY").format("DD-MM-YYYY")} 00:00`,
      to_date: moment(form.toDate, "DD-MM-YYYY").format("DD-MM-YYYY"),
      currency: "EUR",
      installments: form.installments,
      certificate_no: vehicle?.certNumber ?? "",
      sticker_no: withSticker ? form.stickerNo || null : null,
      insurer: {
        person_id: owner?.pid ?? "",
        // NB: backend holder maps legal id_type to "EIK" (the L-Ins schema enum); the
        // page has historically sent "BULSTAT". Left as-is pending live verification.
        id_type: owner?.personType === "2" ? "BULSTAT" : "EGN",
        // Prefer the backend-mapped person type (holder.providers.levins).
        person_type: (calcState?.holder?.providers?.levins?.personType ??
          (owner?.personType === "2" ? "2" : "1")) as "1" | "2",
        name: owner?.name ?? "",
        gname: form.gname,
        sname: form.sname,
        fname: form.fname,
        sex: form.sex,
        addresses: owner?.address
          ? [{
              address_type: "H",
              country_id: "BG",
              country_name: "България",
              address: owner.address,
            }]
          : [],
      },
      objects: [
        {
          reg_no: vehicle?.regNo ?? "",
          cert_no: vehicle?.certNumber ?? "",
          brand: vehicle?.make ?? "",
          model: vehicle?.model ?? "",
          chassis: vehicle?.chassis ?? "",
          seats_num: String(vehicle?.seats ?? ""),
          ccm_ton: String(vehicle?.displacement ?? ""),
          engine_power: String(vehicle?.enginePower ?? ""),
          prod_year: String(vehicle?.productionYear ?? ""),
          first_reg: vehicle?.firstRegistrationDate
            ? moment(vehicle.firstRegistrationDate).format("DD-MM-YYYY")
            : "",
          usage_id: form.usageId,
          fuel_type_id: form.fuelTypeId,
          drive_region_id: form.driveRegionId,
          wheel_position_id: form.rightHandDrive ? "2" : "1",
          // TODO: hardcoded nomenclature defaults — resolve via the
          // LevInsIntegrationService nomenclature endpoints (getVehicleTypes,
          // getVehicleBodyTypes, getVehicleColors, getVehicleRegTypes)
          // before issuing anything other than a standard passenger car.
          type_id: "1",
          body_type_id: "1",
          reg_type_id: "1",
          color_id: "1",
          paint_type_id: "1",
          tonage: "0",
          owners: owner
            ? [{
                person_id: owner.pid,
                name: owner.name,
                gname: form.gname,
                sname: form.sname,
                fname: form.fname,
                own_percent: 100,
              }]
            : [],
          vehicle_users: [],
          insured_seats: null,
          modification: null,
          engine: null,
        },
      ],
      covers: [{ cover_id: "MPSTPL", limit: "0" }],
      corrections: [],
    };
  };

  // ── Calculate premium ──────────────────────────────────────────────────────

  const calcPremium = async () => {
    if (!calcState?.vehicle || !calcState?.owner) {
      setAlert("Липсват данни. Моля, върнете се към калкулатора.");
      setAlertType(FadeAlertType.error);
      return;
    }
    setPremiumLoading(true);
    setPremiumResult(null);
    setIssuedData(null);
    try {
      const result = await LevInsIntegrationService.getPremium(jwt, buildPolicyData(false));
      setPremiumResult(result);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при изчисляване на премия");
      setAlertType(FadeAlertType.error);
    } finally {
      setPremiumLoading(false);
    }
  };

  // ── Issue policy ───────────────────────────────────────────────────────────

  const savePolicy = async () => {
    if (!form.stickerNo.trim()) {
      setAlert("Въведете номер на стикер преди издаване.");
      setAlertType(FadeAlertType.warning);
      return;
    }
    setIssueLoading(true);
    try {
      const result = await LevInsIntegrationService.savePolicy(jwt, buildPolicyData(true));
      setIssuedData(result);
      setAlert("Полицата е издадена успешно.");
      setAlertType(FadeAlertType.success);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при издаване на полица");
      setAlertType(FadeAlertType.error);
    } finally {
      setIssueLoading(false);
    }
  };

  // ── Print offer PDF ────────────────────────────────────────────────────────

  const printOffer = async () => {
    try {
      const blob = await LevInsIntegrationService.printOffer(jwt, buildPolicyData(false));
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при печат");
      setAlertType(FadeAlertType.error);
    }
  };

  // Auto-calculate when arriving from calculator
  useEffect(() => {
    if (!calcState || autoCalcRef.current) return;
    autoCalcRef.current = true;
    const timer = setTimeout(() => calcPremium(), 300);
    return () => clearTimeout(timer);
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  const premiumTotal = premiumResult?.result?.PREMIUM_TOTAL
    ? parseFloat(premiumResult.result.PREMIUM_TOTAL)
    : null;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumb pageName="Лев Инс — Гражданска отговорност" />

      <FadeAlert alert={alert} type={alertType} onClose={() => setAlert(null)} />

      {/* ── Summary from calculator ── */}
      {calcState && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за МПС" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 pt-2">
              <InfoRow label="Рег. номер" value={calcState.vehicle.regNo} />
              <InfoRow label="Шаси" value={calcState.vehicle.chassis} />
              <InfoRow label="Марка / Модел" value={`${calcState.vehicle.make} ${calcState.vehicle.model}`.trim()} />
              <InfoRow label="Куб. см" value={calcState.vehicle.displacement || "—"} />
              <InfoRow label="Мощност (к.с.)" value={calcState.vehicle.enginePower || "—"} />
              <InfoRow label="Места" value={calcState.vehicle.seats || "—"} />
              <InfoRow label="Год. производство" value={calcState.vehicle.productionYear || "—"} />
            </div>
          </fieldset>
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за клиент" />
            <div className="grid grid-cols-1 gap-y-2 p-3 pt-2">
              <div className="mb-1 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2b6cb0]/10 text-sm font-bold text-[#2b6cb0]">
                  {calcState.owner.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-bold text-black dark:text-white">{calcState.owner.name}</p>
                  <p className="text-body dark:text-bodydark text-[11px]">
                    {calcState.owner.personType === "1" ? "Физическо лице" : "Юридическо лице"}
                  </p>
                </div>
              </div>
              <InfoRow label={calcState.owner.personType === "1" ? "ЕГН" : "ЕИК"} value={calcState.owner.pid} />
            </div>
          </fieldset>
        </div>
      )}

      {/* ── Policy parameters ── */}
      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mb-4 rounded-sm border bg-white">
        <SectionHeader title="Параметри на полицата — Лев Инс" />
        <div className="grid grid-cols-1 gap-3 p-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={labelClass}>Начало</label>
            <DatePicker name="fromDate" value={form.fromDate} onChange={(e) => set("fromDate", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Край</label>
            <DatePicker name="toDate" value={form.toDate} onChange={(e) => set("toDate", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Брой вноски</label>
            <SelectGroup
              value={String(form.installments)}
              onChange={(v) => set("installments", Number(v))}
              options={{ "1": "1 вноска", "2": "2 вноски", "4": "4 вноски", "12": "12 вноски" }}
            />
          </div>
          <div>
            <label className={labelClass}>Ползване на МПС</label>
            <SelectGroup
              value={form.usageId}
              onChange={(v) => set("usageId", v)}
              options={{ "1": "Лична употреба", "2": "Учебен", "3": "Такси", "4": "На лизинг", "8": "Търговски" }}
            />
          </div>
          <div>
            <label className={labelClass}>Вид гориво</label>
            <SelectGroup
              value={form.fuelTypeId}
              onChange={(v) => set("fuelTypeId", v)}
              options={{ "B": "Бензин", "D": "Дизел", "E": "Електрически", "GB": "Газ-бензин", "GD": "Газ-дизел" }}
            />
          </div>
          <div>
            <label className={labelClass}>Пол на собственика</label>
            <SelectGroup
              value={form.sex}
              onChange={(v) => set("sex", v as "1" | "2")}
              options={{ "2": "Мъж", "1": "Жена" }}
            />
          </div>
          <div>
            <label className={labelClass}>Собствено име</label>
            <input type="text" value={form.gname} onChange={(e) => set("gname", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Презиме</label>
            <input type="text" value={form.sname} onChange={(e) => set("sname", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Фамилия</label>
            <input type="text" value={form.fname} onChange={(e) => set("fname", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>№ Стикер (за издаване)</label>
            <input
              type="text"
              value={form.stickerNo}
              onChange={(e) => set("stickerNo", e.target.value)}
              placeholder="BG..."
              className={inputClass}
            />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <input
              type="checkbox"
              id="rightHandDrive"
              checked={form.rightHandDrive}
              onChange={(e) => set("rightHandDrive", e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="rightHandDrive" className={labelClass + " mb-0 cursor-pointer"}>Десен волан</label>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 px-4 pb-4">
          <button
            onClick={calcPremium}
            disabled={premiumLoading}
            className="flex items-center gap-2 rounded bg-[#2b6cb0] px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
          >
            {premiumLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {premiumLoading ? "Изчислява..." : "Изчисли премия"}
          </button>

          {premiumTotal !== null && (
            <>
              <button
                onClick={printOffer}
                className="rounded border border-[#2b6cb0] px-6 py-2 text-sm font-semibold text-[#2b6cb0] hover:bg-[#2b6cb0]/10"
              >
                Печат оферта
              </button>
              <button
                onClick={savePolicy}
                disabled={issueLoading || !form.stickerNo.trim() || !!issuedData}
                className="flex items-center gap-2 rounded bg-green-600 px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
                title={!form.stickerNo.trim() ? "Въведете № Стикер" : ""}
              >
                {issueLoading
                  ? "Издава..."
                  : issuedData
                    ? "Издадена ✓"
                    : "Издай полица"}
              </button>
            </>
          )}
        </div>
      </fieldset>

      {/* ── Premium result ── */}
      {premiumTotal !== null && (
        <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
          <SectionHeader title="Резултат от Лев Инс" />
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-sm bg-[#2b6cb0]/10 px-6 py-4 text-center">
                <p className="text-body text-[11px] uppercase tracking-wide">Обща премия</p>
                <p className="text-2xl font-bold text-[#2b6cb0]">{premiumTotal.toFixed(2)} EUR</p>
                {form.installments > 1 && (
                  <p className="text-body text-xs">
                    {(premiumTotal / form.installments).toFixed(2)} EUR / вноска × {form.installments}
                  </p>
                )}
              </div>
              {!form.stickerNo.trim() && (
                <p className="text-body text-xs text-amber-600 dark:text-amber-400">
                  Въведете № на стикер за да издадете полицата.
                </p>
              )}
            </div>
          </div>
        </fieldset>
      )}
    </>
  );
};

export default LevIns;
